import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import MicNoneIcon from '@mui/icons-material/MicNone';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import { Link as RouterLink } from 'react-router-dom';
import logo from '../assets/images/transcriber-logo.svg';

export default function LandingPage() {
  const heroHighlights = [
    'Real-time transcription while you speak',
    'AI summaries with key decisions and action items',
    'Designed for mobile-first meeting workflows',
  ];

  const features = [
    {
      title: 'Live Meeting Capture',
      description:
        'Record conversations and see transcript updates instantly so nothing gets missed.',
      icon: <MicNoneIcon fontSize="large" sx={{ color: 'primary.main' }} />,
    },
    {
      title: 'AI-Powered Summaries',
      description:
        'Convert long discussions into concise summaries, key takeaways, and next steps.',
      icon: <AutoAwesomeIcon fontSize="large" sx={{ color: 'primary.main' }} />,
    },
    {
      title: 'Meeting History',
      description: 'Access past transcripts quickly with organized history and easy retrieval.',
      icon: <HistoryEduIcon fontSize="large" sx={{ color: 'primary.main' }} />,
    },
    {
      title: 'Privacy-First Architecture',
      description:
        'Audio is processed in real time and not stored on servers, while transcripts stay under your control.',
      icon: <GppGoodOutlinedIcon fontSize="large" sx={{ color: 'primary.main' }} />,
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Start or Join a Meeting',
      description: 'Open Genie Transcribe and begin recording from your mobile workflow.',
    },
    {
      step: '02',
      title: 'Capture and Transcribe Live',
      description: 'Speech is transformed into text in real time with minimal delay.',
    },
    {
      step: '03',
      title: 'Review Insights and Share',
      description: 'Generate structured summaries and keep your team aligned instantly.',
    },
  ];

  const useCases = [
    'Client discovery and requirement calls',
    'Internal standups and planning meetings',
    'Interview and feedback conversations',
    'Training sessions and knowledge transfer',
  ];

  return (
    <Box sx={{ backgroundColor: 'background.default' }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 }, position: 'relative' }}>
          <Grid container spacing={{ xs: 4, md: 6 }} justifyContent="center" alignItems="center">
            <Grid item xs={12} md={10} lg={8}>
              <Stack
                spacing={3}
                alignItems='center'
                textAlign='center'
              >
                <Chip
                  label="VendX Genie Transcribe"
                  color="primary"
                  sx={{ alignSelf: 'center', fontWeight: 700, py: 1.25, px: 2 }}
                />
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontSize: { xs: '2.3rem', sm: '2.8rem', md: '3.6rem' },
                    fontWeight: 600,
                    lineHeight: 1.05,
                    color: 'text.primary',
                    textAlign: 'center',
                  }}
                >
                  Turn every conversation into clear, actionable notes.
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ color: 'text.secondary', fontWeight: 400, maxWidth: 680, lineHeight: 1.7, textAlign: 'center' }}
                >
                  Genie Transcribe captures voice in real time, generates reliable transcripts, and
                  delivers AI-powered summaries for faster decisions.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: 'background.paper', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack direction='row' spacing={4} alignItems="center">
            <Box>
              <Stack spacing={2}>
                <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  Built for teams
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, maxWidth: 560 }}>
                  Built for teams who need clarity after every call.
                </Typography>
                <Typography color="text.secondary" sx={{ maxWidth: 560, lineHeight: 1.75 }}>
                  Genie Transcribe keeps every meeting note accurate and actionable with live transcripts,
                  instant summaries, and secure conversation records for teams that move fast.
                </Typography>
                <Stack spacing={1} sx={{ pt: 1 }}>
                  <Typography>
                    • Capture every point without interrupting the flow of the conversation.
                  </Typography>
                  <Typography>
                    • Turn meeting audio into structured notes and priority actions automatically.
                  </Typography>
                  <Typography>
                    • Keep your team aligned with less manual follow-up and more clarity.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            <Box flex={1}>
              <Card
                sx={{
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 24px 60px rgba(16, 64, 125, 0.12)',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ background: 'linear-gradient(180deg, #ffffff 0%, #eff5ff 100%)', p: { xs: 3, md: 4 } }}>
                  <Stack spacing={2}>
                    <Box component="img" src={logo} alt="VendX Genie logo" sx={{ width: 130, height: 'auto' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Actionable meeting clarity for teams.
                    </Typography>
                    <Divider />
                    <Stack spacing={1.25}>
                      <Typography color="text.secondary">Real-time transcript streaming</Typography>
                      <Typography color="text.secondary">Smart summaries and highlights</Typography>
                      <Typography color="text.secondary">Meeting history across sessions</Typography>
                      <Typography color="text.secondary">Secure handling of sensitive voice data</Typography>
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, pt: 3 }}>
                      <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>Transcript accuracy</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>99.3%</Typography>
                      </Box>
                      <Box sx={{ p: 2, backgroundColor: 'rgba(16, 56, 142, 0.08)', borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>Live processing</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>Sub-second</Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Card>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: 'grey.50', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack spacing={1} sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Why Teams Choose Genie Transcribe
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 720, mx: 'auto !important' }}>
              Designed with the same product style as your app to keep experiences consistent from
              landing page to in-app workflow.
            </Typography>
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 3,
            }}
          >
            {features.map((feature) => (
              <Card
                key={feature.title}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 20px 40px rgba(15, 76, 165, 0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 240 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      display: 'grid',
                      placeItems: 'center',
                      backgroundColor: 'rgba(59, 130, 246, 0.12)',
                      color: 'primary.main',
                      mb: 1,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ flexGrow: 1 }}>
                    {feature.description}
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      height: 1,
                      backgroundColor: 'divider',
                      mt: 2,
                    }}
                  />
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Stack spacing={1} sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            How It Works
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 720, mx: 'auto !important' }}>
            Simple workflow with strong output quality and better meeting follow-through.
          </Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          {process.map((item, index) => (
            <Card
              key={item.step}
              sx={{
                minHeight: 240,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor:
                      index === 0
                        ? 'rgba(59, 130, 246, 0.16)'
                        : index === 1
                          ? 'rgba(37, 99, 235, 0.16)'
                          : 'rgba(29, 78, 216, 0.16)',
                    color: 'primary.main',
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>{item.step}</Typography>
                </Box>
                <Typography variant="h6" sx={{ mb: 0, fontWeight: 700 }}>
                  {item.title}
                </Typography>
                <Typography color="text.secondary" sx={{ flexGrow: 1 }}>
                  {item.description}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: 1,
                    backgroundColor: 'divider',
                    mt: 1,
                  }}
                />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>

      <Box sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                Built for Trust and Data Responsibility
              </Typography>
              <Typography sx={{ opacity: 0.95, maxWidth: 680, lineHeight: 1.8 }}>
                Genie Transcribe follows a privacy-focused model: audio is processed for transcription and
                not stored on servers, while transcripts remain manageable by users with deletion controls.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card
                sx={{
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  boxShadow: 'none',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography sx={{ fontWeight: 700, mb: 2.5, color: 'white' }}>Ideal For</Typography>
                  <Stack spacing={1.25}>
                    {useCases.map((useCase) => (
                      <Typography key={useCase} sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        • {useCase}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
